import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StickerLogo = () => (
  <img src="/brand/lani-logo.png" alt="Lani Lemonade" className="st-logo-img" data-testid="sticker-logo" />
);

const TEST_ORDER = {
  id: "testlabel000",
  customer_name: "Test Print",
  items: [{ id: "test-cup", brand: "lemonade", name: "Classic — 34oz", qty: 1, variant: "Ice: Regular · Regular sugar" }],
};

export default function PrintStickers() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const isTest = orderId === "test";
  const [searchParams] = useSearchParams();
  const manual = searchParams.get("manual") === "1";

  useEffect(() => {
    if (isTest) {
      setOrder({ ...TEST_ORDER, created_at: new Date().toISOString() });
      return;
    }
    const token = localStorage.getItem("gb-admin-token");
    axios
      .get(`${API}/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setOrder(r.data))
      .catch(() => {
        try {
          const queued = (JSON.parse(localStorage.getItem("gb-pos-queue")) || []).find((q) => q.order_id === orderId);
          if (queued) {
            setOrder({ id: queued.order_id, customer_name: queued.customer_name, items: queued.items, created_at: queued.created_offline_at });
            return;
          }
        } catch {}
        setError("Could not load this order — sign in at /pos or /admin first, then retry.");
      });
  }, [orderId]);

  const stickers = useMemo(() => {
    if (!order) return [];
    const out = [];
    order.items
      .filter((i) => i.brand === "lemonade")
      .forEach((i) => {
        const parts = (i.variant || "").split(" · ").filter(Boolean);
        const picks = parts.filter((p) => p.startsWith("#")).map((p) => p.replace(/^#\d+\s*/, ""));
        const extras = parts.filter((p) => !p.startsWith("#"));
        for (let n = 1; n <= i.qty; n++) {
          out.push({
            key: `${i.id}-${n}`,
            drink: picks[n - 1] || i.name.replace(" — 34oz", ""),
            extras,
          });
        }
      });
    out.forEach((s, idx) => {
      s.num = idx + 1;
      s.total = out.length;
    });
    return out;
  }, [order]);

  useEffect(() => {
    if (order && stickers.length && !manual) {
      const t = setTimeout(() => window.print(), 700);
      const back = () => navigate("/pos");
      window.addEventListener("afterprint", back);
      return () => {
        clearTimeout(t);
        window.removeEventListener("afterprint", back);
      };
    }
  }, [order, stickers.length, manual]);

  if (error) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-400 px-6 text-center">{error}</div>;
  if (!order) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">Loading stickers…</div>;

  const short = isTest ? "TEST" : order.id.slice(0, 8);
  const when = isTest ? "Label check" : new Date(order.created_at).toLocaleString();

  return (
    <div className="py-8">
      <div id="print-toolbar" className="max-w-xl mx-auto px-4 mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-extrabold text-xl text-white">Cup Stickers — {order.customer_name}</h1>
          <p className="text-sm text-zinc-500">Order #{short} · {stickers.length} sticker{stickers.length === 1 ? "" : "s"} · 2.25″ × 1.25″ labels</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="print-button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-xl bg-white text-black font-bold px-5 py-3 active:scale-95 transition-transform"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
          {!isTest && (
            <button
              data-testid="print-test-label"
              onClick={() => navigate("/print/test")}
              className="inline-flex items-center gap-2 rounded-xl border border-amber-400/40 text-amber-300 font-bold px-5 py-3 active:scale-95 transition-transform"
            >
              <Printer className="w-4 h-4" /> Test Label
            </button>
          )}
          <button
            data-testid="back-to-pos"
            onClick={() => navigate("/pos")}
            className="inline-flex items-center gap-2 rounded-xl border border-white/20 text-white font-bold px-5 py-3 active:scale-95 transition-transform"
          >
            <ArrowLeft className="w-4 h-4" /> Register
          </button>
        </div>
      </div>
      {stickers.length === 0 && (
        <p className="text-center text-zinc-500" data-testid="no-stickers">No lemonade cups on this order — nothing to print.</p>
      )}
      <div id="print-area" className="flex flex-wrap justify-center gap-4 px-4" data-testid="stickers-area">
        {stickers.map((s) => (
          <div className="sticker" key={s.key} data-testid={`sticker-${s.key}`}>
            <StickerLogo />
            <p className="st-order">#{short} · {when}</p>
            <p className="st-name">{order.customer_name}</p>
            <p className="st-drink">{s.drink}</p>
            {s.extras.map((e, i) => (
              <p className={`st-extra${/keto/i.test(e) ? " st-strong" : ""}`} key={i}>{e}</p>
            ))}
            <p className="st-cup">{s.num} of {s.total}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
